'use client'

import { StudioConfigProvider, useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { CoinTopUpDialog } from '@repo/studio-components/coin-top-up-dialog'
import { Toaster, toast } from '@repo/studio-components-toast'
import {
  closeModerationDialog,
  openPromptModerationDialog,
  openUploadModerationDialog,
  useModerationDialogStore,
} from '@repo/studio-components-upload-moderation-dialog/store'
import {
  openCoinTopUpDialog,
  useCoinTopUpDialogStore,
} from '@repo/studio-hooks-coins/use-coin-top-up-dialog'
import {
  buildStatusDialogsResultFromStripeInvoicePayUrl,
  closePaymentRequiredDialog,
  openPaymentRequiredDialog,
  usePaymentRequiredDialogStore,
} from '@repo/studio-hooks-payments/use-payment-required-dialog'
import { Button } from '@repo/studio-design-system-ui-button'
import { createFlatStudioTranslate } from '@repo/studio-i18n-hooks/shell-translations'
import { ActivateTrialStatusDialogs } from 'payments/activate-trial-button/components/activate-trial-status-dialogs'
import { usePaymentsDialog } from 'payments/components/payments-dialog-wrapper-context'
import { CoinSuccessDialog } from 'payments/payments-dialog/components/coin-success-dialog'
import * as Dialog from 'primitives/dialog'
import * as React from 'react'
import { classifyMarketplaceToolError } from './marketplace-tool-error'

const DIALOG_Z_INDEX = 10_000

/**
 * Hosts the Studio generation-error dialogs on the marketplace tool pages.
 *
 * The dialogs resolve their copy through `StudioConfig.t` rather than their own
 * i18n library, so the Studio `studio.*` strings reach them as the flat map the
 * tool layout loaded on the server — this app has no react-i18next instance.
 */
export function MarketplaceToolErrors({
  children,
  shellTranslations,
}: React.PropsWithChildren<{ shellTranslations: Record<string, string> }>) {
  const t = React.useMemo(() => createFlatStudioTranslate(shellTranslations), [shellTranslations])
  const config = React.useMemo(() => ({ t }), [t])

  return (
    <StudioConfigProvider config={config}>
      {children}
      <MarketplaceToolErrorDialogs />
      <Toaster />
    </StudioConfigProvider>
  )
}

export function useMarketplaceToolError() {
  const paymentsModal = usePaymentsDialog()
  const t = useStudioTranslate()

  return React.useCallback(
    (error: unknown, fallbackMessage: string) => {
      const action = classifyMarketplaceToolError(error)

      if (action.type === 'studio-ai') {
        void toast({
          title: t('studio.common.ai_subscription_required.title'),
          description: t('studio.common.ai_subscription_required.content'),
        })
        return action.type
      }

      if (action.type === 'all-access') {
        paymentsModal.open()
        return action.type
      }

      if (action.type === 'payment-required') {
        openPaymentRequiredDialog(
          buildStatusDialogsResultFromStripeInvoicePayUrl(action.payUrl, action.status),
        )
        return action.type
      }

      if (action.type === 'insufficient-funds') {
        openCoinTopUpDialog()
        if (action.requiredBalance !== null) {
          void toast({
            title: t('studio.common.error_insufficient_coins.title'),
            description: t('studio.common.error_insufficient_coins.content', {
              count: action.requiredBalance,
            }),
          })
        }
        return action.type
      }

      if (action.type === 'nsfw') {
        void toast({
          title: t('studio.common.error_nsfw.title'),
          description: t('studio.common.error_nsfw.content'),
        })
        return action.type
      }

      if (action.type === 'upload-minor') {
        openUploadModerationDialog()
        return action.type
      }

      if (action.type === 'prompt-minor') {
        openPromptModerationDialog()
        return action.type
      }

      void toast({
        title: fallbackMessage,
        description: '',
      })
      return action.type
    },
    [paymentsModal, t],
  )
}

function MarketplaceToolErrorDialogs() {
  const coinTopUpOpen = useCoinTopUpDialogStore(state => state.isOpen)
  const paymentResult = usePaymentRequiredDialogStore(state => state.result)
  const [showCoinSuccess, setShowCoinSuccess] = React.useState(false)

  function handleAutoStartSuccess() {
    useCoinTopUpDialogStore.setState({ isOpen: false })
    setShowCoinSuccess(true)
  }

  return (
    <>
      <CoinTopUpDialog
        open={coinTopUpOpen}
        onOpenChange={open => useCoinTopUpDialogStore.setState({ isOpen: open })}
        onAutoStartSuccess={handleAutoStartSuccess}
        zIndex={DIALOG_Z_INDEX}
      />
      <CoinSuccessDialog open={showCoinSuccess} onOpenChange={setShowCoinSuccess} />
      <MarketplaceModerationDialog />
      {paymentResult ? (
        <ActivateTrialStatusDialogs
          onErrorClose={closePaymentRequiredDialog}
          onSuccessOpenChange={closePaymentRequiredDialog}
          result={paymentResult}
          subscriptionType="studio-ai"
        />
      ) : null}
    </>
  )
}

function MarketplaceModerationDialog() {
  const open = useModerationDialogStore(state => state.open)
  const variant = useModerationDialogStore(state => state.variant)
  const t = useStudioTranslate()
  const key =
    variant === 'upload'
      ? 'studio.common.error_upload_contains_minor'
      : 'studio.common.error_prompt_contains_minor'

  return (
    <Dialog.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          closeModerationDialog()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Body zIndex={DIALOG_Z_INDEX} overlayZIndex={DIALOG_Z_INDEX}>
          <div className="rounded-4 flex w-full max-w-[480px] flex-col gap-4 bg-gray-900 p-6">
            <div className="flex flex-col gap-2">
              <span className="text-gray-50 typography-20">{t(`${key}.title`)}</span>
              <span className="text-gray-400 typography-14">{t(`${key}.content`)}</span>
            </div>
            <Dialog.Close asChild>
              <Button className="self-end">{t(`${key}.cta`)}</Button>
            </Dialog.Close>
          </div>
        </Dialog.Body>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
